import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, Menu, X } from "lucide-react";
import { useState } from "react";
import VadimChat from "@/components/VadimChat";
import { useLive, useServiceInfo } from "@/lib/serviceInfo";

const navLinks = [
  { href: "/", label: "Hjem" },
  { href: "/tjenester", label: "Tjenester" },
  { href: "/innlevering", label: "Innlevering" },
  { href: "/viktig-info", label: "Viktig info" },
  { href: "/bedrift", label: "Bedrift" },
  { href: "/profesjonelle-kaffemaskiner", label: "Profesjonell" },
  { href: "/transport", label: "Transport" },
  { href: "/booking", label: "Booking" },
  { href: "/sporing", label: "Sporing" },
  { href: "/verksted", label: "Verksted" },
  { href: "/kontakt", label: "Kontakt" },
  { href: "/faq", label: "Hjelp / FAQ" },
  { href: "/kontakt-skjema", label: "Kontakt oss" },
];
const headerLinks = navLinks.filter((l) => l.href !== "/kontakt-skjema");
export default function Layout({ children }) {
  const live = useLive();
  const notices = useServiceInfo()?.temporary_notices || [];
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-[#FFF8F0]">
      {notices.map((n) => (
        <div key={`${n.start_date}-${n.notice}`} className="bg-[#3D2314] text-white text-sm px-4 py-2 text-center" data-testid="site-temporary-notice">
          <span className="font-semibold">{n.start_date === n.end_date ? n.start_date.split("-").reverse().join(".") : `${n.start_date.split("-").reverse().join(".")}–${n.end_date.split("-").reverse().join(".")}`}:</span> {n.notice}
        </div>
      ))}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#D4A574]/20 shadow-sm">
        <div className="max-w-7xl 2xl:max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex flex-col shrink-0 mr-4" data-testid="logo-link">
              <div className="flex items-baseline gap-1 whitespace-nowrap">
                <span className="text-lg font-bold text-[#3D2314] font-display">Bergen</span>
                <span className="text-sm font-light text-[#D4A574] hidden sm:inline">Kaffemaskin- og Teknikk Service</span>
              </div>
              <span className="text-[10px] text-[#1A1A1A]/50 tracking-wide hidden md:block">
                Kaffemaskiner • Symaskiner • Storkjøkkenmaskiner • Teknisk service
              </span>
            </Link>

            <nav className="hidden 2xl:flex items-center gap-3.5">
              {headerLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.href.replace(/\//g, "") || "home"}`}
                  className={`text-[13px] font-medium whitespace-nowrap transition-colors hover:text-[#D4A574] ${
                    location.pathname === link.href ? "text-[#D4A574]" : "text-[#1A1A1A]/70"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-2 2xl:gap-3 shrink-0 ml-auto 2xl:ml-3">
              <a href="tel:90232206" className="hidden 2xl:flex items-center gap-1 text-sm text-[#3D2314] whitespace-nowrap" data-testid="header-phone">
                <Phone className="h-4 w-4" />
                90 23 22 06
              </a>
              <Button asChild className="hidden sm:inline-flex bg-[#3D2314] hover:bg-[#3D2314]/90 text-white whitespace-nowrap">
                <Link to="/innlevering" data-testid="header-cta">Lever inn utstyr</Link>
              </Button>
            </div>

            <button
              className="2xl:hidden p-2"
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="2xl:hidden border-t border-[#D4A574]/20 bg-white">
            <nav className="flex flex-col px-4 py-3 gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`py-2 px-3 rounded-md text-sm font-medium ${
                    location.pathname === link.href ? "bg-[#D4A574]/10 text-[#3D2314]" : "text-[#1A1A1A]/70"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Button asChild className="bg-[#3D2314] hover:bg-[#3D2314]/90 text-white mt-2">
                <Link to="/innlevering">Lever inn utstyr</Link>
              </Button>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <VadimChat />

      <footer className="bg-[#3D2314] text-white/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-bold text-white mb-3 font-display">Bergen Kaffemaskin- og Teknikk Service</h3>
              <p className="text-xs text-[#D4A574] mb-1">Kaffemaskiner • Symaskiner • Storkjøkkenmaskiner • Teknisk service</p>
              <p className="text-sm text-white/70 mb-2">
                Profesjonell reparasjon og service av kaffemaskiner, symaskiner og storkjøkkenmaskiner for hjem, bedrifter, hoteller, kafeer, restauranter, båter og bensinstasjoner i Bergen, Hordaland og Vestland.
              </p>
              <p className="text-xs text-white/60 mt-2">Vadims Harjkovs Multihåndverk &amp; El-Tjenester · Org.nr 935 886 600</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white mb-3 font-display">Snarveier</h3>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="text-sm text-white/70 hover:text-[#D4A574] transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/vilkar" className="text-sm text-white/70 hover:text-[#D4A574] transition-colors">Vilkår</Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white mb-3 font-display">Kontakt</h3>
              <ul className="space-y-2 text-sm text-white/70">
                <li>Birkeveien 2, 5093 Bergen</li>
                <li><a href="tel:90232206" className="hover:text-[#D4A574]">Tlf: 90 23 22 06</a></li>
                <li><a href="mailto:info@bergenkaffeogteknikk.no" className="hover:text-[#D4A574]">info@bergenkaffeogteknikk.no</a></li>
                <li>Man–Fre: {live.hoursWeekday}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 mt-8 pt-6 text-center text-sm text-white/50">
            © {new Date().getFullYear()} Bergen Kaffemaskin- og Teknikk Service. Alle rettigheter reservert.
          </div>
        </div>
      </footer>
    </div>
  );
}
